import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { adminApi } from '../lib/api.ts';
import { Btn } from '../components/Btn.tsx';
import { LinkBtn } from '../components/LinkBtn.tsx';
import { CenteredSpinner, Spinner } from '../components/Spinner.tsx';
import { Modal } from '../components/Modal.tsx';
import { Field, Select, TextArea, TextInput } from '../components/Field.tsx';
import { ErrorMessage } from '../components/ErrorMessage.tsx';
import { SubmissionStatusBadge } from '../components/StatusBadge.tsx';
import { InfoRow, ReadOnlyValue } from '../components/InfoRows.tsx';
import { EntityQuestionnaireTabs } from './EntityQuestionnaireTabs.tsx';
import { QuestionReviewButtons } from './QuestionReviewButtons.tsx';
import { buildEntityQuestionnaireTabs, type QuestionnaireGroup } from '../lib/entityQuestionnaireTabs.ts';
import type { ReviewPayload } from './useReviewMutation.ts';
import type {
  AdminQuestionnaire,
  AdminUserListItem,
  AssetAudit,
  AssetAuditStatus,
  AssetDetail,
  AssetSubmission,
  CompanyDocument,
  Question,
  QuestionCategory,
  Questionnaire,
} from '../lib/types.ts';

export function AdminAssetDetail() {
  const { id = '' } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const [editDetails, setEditDetails] = useState(false);

  const assetQuery = useQuery({
    queryKey: ['asset', id],
    queryFn: () => adminApi.get<{ asset: AssetDetail }>(`/api/assets/${id}`),
    enabled: !!id,
  });
  const questionnairesQuery = useQuery({
    queryKey: ['asset', id, 'questionnaires'],
    queryFn: () =>
      adminApi.get<{ questionnaires: AdminQuestionnaire[] }>(`/api/assets/${id}/questionnaires`),
    enabled: !!id,
  });
  const questionsQuery = useQuery({
    queryKey: ['questions'],
    queryFn: () => adminApi.get<{ questions: Question[] }>('/api/questions'),
  });
  const categoriesQuery = useQuery({
    queryKey: ['question-categories'],
    queryFn: () => adminApi.get<{ categories: QuestionCategory[] }>('/api/question-categories'),
  });
  const submissionsQuery = useQuery({
    queryKey: ['asset-submissions', id],
    queryFn: () =>
      adminApi.get<{ submissions: AssetSubmission[] }>(`/api/assets/${id}/submissions`),
    enabled: !!id,
  });
  const documentsQuery = useQuery({
    queryKey: ['documents'],
    queryFn: () => adminApi.get<{ documents: CompanyDocument[] }>('/api/documents'),
  });

  const reviewMutation = useMutation({
    mutationFn: ({ questionId, payload }: { questionId: string; payload: ReviewPayload }) =>
      adminApi.post(`/api/assets/${id}/submissions/${questionId}/review`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['asset-submissions', id] }),
  });

  const questionnaires = questionnairesQuery.data?.questionnaires ?? [];
  const questions = questionsQuery.data?.questions ?? [];
  const categories = categoriesQuery.data?.categories ?? [];
  const submissions = submissionsQuery.data?.submissions ?? [];
  const documents = documentsQuery.data?.documents ?? [];

  const groups = useMemo(
    () => buildEntityQuestionnaireTabs(questionnaires, categories, questions),
    [questionnaires, categories, questions],
  );
  const subByQ = useMemo(() => {
    const m = new Map<string, AssetSubmission>();
    for (const s of submissions) m.set(s.question_id, s);
    return m;
  }, [submissions]);

  if (
    assetQuery.isLoading ||
    questionnairesQuery.isLoading ||
    questionsQuery.isLoading ||
    categoriesQuery.isLoading ||
    submissionsQuery.isLoading
  ) {
    return <CenteredSpinner />;
  }
  const v = assetQuery.data?.asset;
  if (!v) return <ErrorMessage error={new Error('Asset not found')} />;

  const title = [v.year, v.make, v.model].filter(Boolean).join(' ') || v.registration_plate || 'Asset';

  // Company-owned assets have answers submitted by the company; admin-owned
  // assets are marked directly by the admin (no answer to show).
  const isCompanyAsset = !!v.company;

  // One review row: status badge, question + answer (for company assets), then
  // the review buttons. For company assets, only show buttons once answered.
  const renderQuestion = (question: Question) => {
    const sub = subByQ.get(question.id);
    const busy = reviewMutation.isPending && reviewMutation.variables?.questionId === question.id;
    return (
      <div className="flex items-start gap-3 py-3">
        <div className="min-w-0 flex-1">
          {sub && (
            <div className="mb-1">
              <SubmissionStatusBadge status={sub.status} />
            </div>
          )}
          <div className="text-sm font-semibold text-slate-900">{question.label}</div>
          {question.help_text && <div className="mt-0.5 text-xs text-slate-500">{question.help_text}</div>}
          {isCompanyAsset && (
            <div className="mt-0.5 text-sm text-slate-600">
              {(() => {
                const hasFiles = (sub?.submission_files?.length ?? 0) > 0;
                if (hasFiles) {
                  return (
                    <span className="flex flex-col gap-0.5">
                      {sub!.submission_files.map((file) => (
                        <button
                          key={file.id}
                          type="button"
                          className="inline-flex items-center gap-1 text-blue-600 hover:underline text-left"
                          onClick={async () => {
                            const res = await adminApi.get<{ url: string }>(
                              `/api/assets/${id}/submissions/${sub!.id}/files/${file.id}/download-url`,
                            );
                            window.open(res.url, '_blank', 'noopener');
                          }}
                        >
                          📎 {file.file_name}
                        </button>
                      ))}
                    </span>
                  );
                }
                return answerPreview(sub);
              })()}
            </div>
          )}
        </div>
        {!isCompanyAsset || sub ? (
          <QuestionReviewButtons
            question={question}
            status={sub?.status}
            documents={documents}
            pending={busy}
            pendingDecision={busy ? reviewMutation.variables?.payload.decision : undefined}
            error={(reviewMutation.error as Error | null) ?? null}
            onDecide={(payload) => reviewMutation.mutate({ questionId: question.id, payload })}
          />
        ) : (
          <span className="text-xs text-slate-400">Awaiting company answer</span>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <Link to="/admin/assets" className="text-sm text-slate-500 hover:underline">
        ← Assets
      </Link>
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">{title}</h1>
          <p className="mt-1 text-sm text-slate-500">
            {v.company?.company_name ?? 'Operating company'} · {v.registration_plate ?? 'no plate'} ·{' '}
            {v.asset_type ?? 'asset'}
          </p>
        </div>
        <Btn variant="secondary" onClick={() => setEditDetails(true)}>
          Edit details
        </Btn>
      </div>

      <EntityQuestionnaireTabs
        infoLabel="Asset information"
        infoPanel={<AssetInfoPanel asset={v} />}
        groups={groups}
        tabBadge={(g) => unreviewedCount(g, subByQ)}
        renderQuestion={renderQuestion}
        ariaLabel="Asset review sections"
      />

      <AssetAuditsSection assetId={id} />

      {editDetails && (
        <EditDetailsModal
          asset={v}
          onClose={() => setEditDetails(false)}
          onSaved={() => qc.invalidateQueries({ queryKey: ['asset', id] })}
        />
      )}
    </div>
  );
}

const AUDIT_STATUS_BADGE: Record<AssetAuditStatus, string> = {
  assigned: 'bg-blue-50 text-blue-700',
  in_progress: 'bg-amber-50 text-amber-700',
  compliant: 'bg-emerald-50 text-emerald-700',
  not_compliant: 'bg-red-50 text-red-700',
};

const AUDIT_STATUS_LABEL: Record<AssetAuditStatus, string> = {
  assigned: 'Assigned',
  in_progress: 'In progress',
  compliant: 'Compliant',
  not_compliant: 'Not compliant',
};

// Audits assigned to this asset. An admin picks an Audit questionnaire + an auditor;
// the auditor performs it from their own portal. Distinct from the per-question
// compliance review above (which the admin fills in directly).
function AssetAuditsSection({ assetId }: { assetId: string }) {
  const qc = useQueryClient();
  const [assignOpen, setAssignOpen] = useState(false);

  const auditsQuery = useQuery({
    queryKey: ['asset-audits', assetId],
    queryFn: () => adminApi.get<{ audits: AssetAudit[] }>(`/api/assets/${assetId}/audits`),
    enabled: !!assetId,
  });
  const audits = auditsQuery.data?.audits ?? [];

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-slate-800">Audits</h3>
          <p className="mt-0.5 text-xs text-slate-500">
            Assign an Audit questionnaire to an auditor to perform on this asset.
          </p>
        </div>
        <Btn size="sm" onClick={() => setAssignOpen(true)}>
          Assign audit
        </Btn>
      </div>

      {auditsQuery.isLoading ? (
        <div className="py-4">
          <Spinner size={20} />
        </div>
      ) : audits.length === 0 ? (
        <p className="text-sm italic text-slate-400">No audits assigned yet.</p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {audits.map((a) => (
            <li key={a.id} className="flex items-center justify-between gap-3 py-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="truncate text-sm font-medium text-slate-900">
                    {a.questionnaire?.name ?? 'Audit'}
                  </span>
                  <span className={`rounded-full px-2 py-0.5 text-xs ${AUDIT_STATUS_BADGE[a.status]}`}>
                    {AUDIT_STATUS_LABEL[a.status]}
                  </span>
                </div>
                <div className="mt-0.5 text-xs text-slate-500">
                  {a.auditor?.name ? `Auditor: ${a.auditor.name}` : 'Unassigned'}
                  {a.assigned_at
                    ? ` · assigned ${new Date(a.assigned_at).toLocaleDateString()}`
                    : ''}
                </div>
              </div>
              <LinkBtn to={`/admin/audits/${a.id}`} size="sm" variant="secondary">
                View
              </LinkBtn>
            </li>
          ))}
        </ul>
      )}

      {assignOpen && (
        <AssignAuditModal
          assetId={assetId}
          onClose={() => setAssignOpen(false)}
          onAssigned={() => {
            void qc.invalidateQueries({ queryKey: ['asset-audits', assetId] });
            setAssignOpen(false);
          }}
        />
      )}
    </div>
  );
}

function AssignAuditModal({
  assetId,
  onClose,
  onAssigned,
}: {
  assetId: string;
  onClose: () => void;
  onAssigned: () => void;
}) {
  const questionnairesQuery = useQuery({
    queryKey: ['questionnaires'],
    queryFn: () => adminApi.get<{ questionnaires: Questionnaire[] }>('/api/questionnaires'),
  });
  const usersQuery = useQuery({
    queryKey: ['users'],
    queryFn: () => adminApi.get<{ users: AdminUserListItem[] }>('/api/users'),
  });

  const auditQuestionnaires = (questionnairesQuery.data?.questionnaires ?? []).filter(
    (q) => q.audience === 'audit' && q.is_active,
  );
  const auditors = (usersQuery.data?.users ?? []).filter((u) => u.role === 'auditor');

  const [questionnaireId, setQuestionnaireId] = useState('');
  const [auditorId, setAuditorId] = useState('');
  const [error, setError] = useState<unknown>(null);

  const assign = useMutation({
    mutationFn: () =>
      adminApi.post(`/api/assets/${assetId}/audits`, {
        questionnaire_id: questionnaireId,
        auditor_user_id: auditorId,
      }),
    onSuccess: onAssigned,
    onError: setError,
  });

  const ready = !!questionnaireId && !!auditorId;

  return (
    <Modal
      open
      onClose={onClose}
      title="Assign audit"
      footer={
        <>
          <Btn variant="ghost" onClick={onClose}>
            Cancel
          </Btn>
          <Btn onClick={() => assign.mutate()} loading={assign.isPending} disabled={!ready}>
            Assign
          </Btn>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Audit questionnaire" required>
          {auditQuestionnaires.length === 0 ? (
            <div className="text-xs italic text-slate-500">
              No Audit questionnaires yet. Create one under Questionnaires → New questionnaire →
              audience “Audit”.
            </div>
          ) : (
            <Select value={questionnaireId} onChange={(e) => setQuestionnaireId(e.target.value)}>
              <option value="">Select a questionnaire…</option>
              {auditQuestionnaires.map((q) => (
                <option key={q.id} value={q.id}>
                  {q.name}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label="Auditor" required>
          {auditors.length === 0 ? (
            <div className="text-xs italic text-slate-500">
              No auditors yet. Invite one under Settings → Users with the “Auditor” role.
            </div>
          ) : (
            <Select value={auditorId} onChange={(e) => setAuditorId(e.target.value)}>
              <option value="">Select an auditor…</option>
              {auditors.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name} ({u.email})
                </option>
              ))}
            </Select>
          )}
        </Field>
        <ErrorMessage error={error} />
      </div>
    </Modal>
  );
}

// Read-only asset detail (edits go through the "Edit details" modal).
function AssetInfoPanel({ asset }: { asset: AssetDetail }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 md:max-w-xl">
      <h3 className="mb-1 text-base font-semibold text-slate-800">Details</h3>
      <InfoRow label="Make"><ReadOnlyValue value={asset.make} /></InfoRow>
      <InfoRow label="Model"><ReadOnlyValue value={asset.model} /></InfoRow>
      <InfoRow label="Year"><ReadOnlyValue value={asset.year ? String(asset.year) : null} /></InfoRow>
      <InfoRow label="Registration plate"><ReadOnlyValue value={asset.registration_plate} /></InfoRow>
      <InfoRow label="Type"><ReadOnlyValue value={asset.asset_type} /></InfoRow>
      <InfoRow label="VIN"><ReadOnlyValue value={asset.vin} /></InfoRow>
      <InfoRow label="Colour"><ReadOnlyValue value={asset.colour} /></InfoRow>
      <InfoRow label="Owner">
        <ReadOnlyValue value={asset.company?.company_name ?? 'Operating company'} />
      </InfoRow>
      <InfoRow label="Status">
        <span
          className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
            asset.compliance_status === 'compliant'
              ? 'bg-emerald-50 text-emerald-700'
              : 'bg-orange-50 text-orange-700'
          }`}
        >
          {asset.compliance_status === 'compliant' ? 'Compliant' : 'Not compliant'}
        </span>
      </InfoRow>
      <InfoRow label="Notes"><ReadOnlyValue value={asset.notes} /></InfoRow>
    </div>
  );
}

// Minimal answer preview for an asset submission (file uploads are handled
// separately as clickable links; this covers non-file answer types).
function answerPreview(sub: AssetSubmission | undefined): string {
  if (!sub) return 'Not answered yet';
  const a = sub.answer;
  if (a === null || a === undefined || a === '') return 'Not answered yet';
  if (Array.isArray(a)) return (a as string[]).join(', ');
  if (typeof a === 'boolean') return a ? 'Yes' : 'No';
  if (typeof a === 'object') return 'Answered';
  return String(a);
}

// Tab badge: questions still needing a first review (no row, or pending).
function unreviewedCount(
  group: QuestionnaireGroup,
  subByQ: Map<string, AssetSubmission>,
): number {
  let n = 0;
  for (const section of group.sections) {
    for (const q of section.questions) {
      const sub = subByQ.get(q.id);
      if (!sub || sub.status === 'pending') n += 1;
    }
  }
  return n;
}

function EditDetailsModal({
  asset,
  onClose,
  onSaved,
}: {
  asset: AssetDetail;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState({
    make: asset.make ?? '',
    model: asset.model ?? '',
    year: asset.year ? String(asset.year) : '',
    registration_plate: asset.registration_plate ?? '',
    asset_type: asset.asset_type ?? '',
    vin: asset.vin ?? '',
    colour: asset.colour ?? '',
    notes: asset.notes ?? '',
  });
  const [error, setError] = useState<unknown>(null);
  const save = useMutation({
    mutationFn: () =>
      adminApi.patch(`/api/assets/${asset.id}`, {
        make: form.make.trim() || null,
        model: form.model.trim() || null,
        year: form.year ? Number(form.year) : null,
        registration_plate: form.registration_plate.trim() || null,
        asset_type: form.asset_type.trim() || null,
        vin: form.vin.trim() || null,
        colour: form.colour.trim() || null,
        notes: form.notes.trim() || null,
      }),
    onSuccess: () => {
      onSaved();
      onClose();
    },
    onError: setError,
  });

  return (
    <Modal
      open
      onClose={onClose}
      title="Edit asset"
      size="lg"
      footer={
        <>
          <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn onClick={() => save.mutate()} loading={save.isPending}>Save</Btn>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-3">
        <Field label="Make"><TextInput value={form.make} onChange={(e) => setForm({ ...form, make: e.target.value })} /></Field>
        <Field label="Model"><TextInput value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} /></Field>
        <Field label="Year"><TextInput type="number" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} /></Field>
        <Field label="Registration plate"><TextInput value={form.registration_plate} onChange={(e) => setForm({ ...form, registration_plate: e.target.value })} /></Field>
        <Field label="Type"><TextInput value={form.asset_type} onChange={(e) => setForm({ ...form, asset_type: e.target.value })} /></Field>
        <Field label="Colour"><TextInput value={form.colour} onChange={(e) => setForm({ ...form, colour: e.target.value })} /></Field>
        <Field label="VIN"><TextInput value={form.vin} onChange={(e) => setForm({ ...form, vin: e.target.value })} /></Field>
        <div className="col-span-2">
          <Field label="Notes"><TextArea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
        </div>
      </div>
      <ErrorMessage error={error} />
    </Modal>
  );
}
